// Kit product drawer — opened by the 👀 icon on a charm card.
// On desktop: slides in from the right.
// On mobile: full-screen popup (closable via overlay/X/Escape).
// Fetches the original product page, extracts the MainProduct section, and replaces the
// native add-to-cart submit with an "Ajouter à mon kit" button. On submit, the chosen
// variant is dispatched back to <kit-builder> via `kit-product-drawer:add`.

if (!customElements.get('kit-product-drawer')) {
  customElements.define(
    'kit-product-drawer',
    class KitProductDrawer extends HTMLElement {
      constructor() {
        super();

        this.contentEl = this.querySelector('[data-product-drawer-content]');
        this.overlayEl = this.querySelector('[data-product-drawer-overlay]');
        this.closeBtn = this.querySelector('[data-product-drawer-close]');

        this.currentSlot = null;

        // Move ourselves to <body> so `position: fixed` is anchored to the viewport, not
        // to a section ancestor with transform/filter/contain.
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
        }

        document.addEventListener('kit-product-drawer:open', (ev) => this.open(ev.detail));
        if (this.overlayEl) this.overlayEl.addEventListener('click', () => this.close());
        if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
        });
      }

      open({ productUrl, slot, productId }) {
        if (!productUrl) return;
        this.currentSlot = slot || 1;
        this.currentProductId = productId || 0;
        this.classList.add('is-open');
        document.body.style.overflow = 'hidden';

        if (this.contentEl) {
          this.contentEl.innerHTML = '<div class="kit-product-drawer__loading">Chargement…</div>';
        }

        fetch(productUrl)
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const main = doc.querySelector('section[id^="MainProduct-"]') || doc.querySelector('.product');
            if (!main) {
              this.contentEl.innerHTML = '<div class="kit-product-drawer__error">Impossible de charger ce produit.</div>';
              return;
            }

            // Avoid id collisions with the host page when re-injecting Shopify's section HTML.
            const sectionId = main.dataset.section || '';
            let scoped = main.outerHTML.replaceAll(sectionId, `kit-pdp-${sectionId}-${Date.now()}`);

            // Cache the variants array BEFORE we mangle the HTML.
            this._variants = this._extractVariants(main);

            // Pull out the product's featured image URL so we can show it as a hero at the
            // top of the drawer, independent of how Shopify renders the gallery.
            this._featuredImageUrl = this._extractFeaturedImage(main);

            // Neutralize Dawn's product-form so its constructor never runs (it would attach a
            // submit listener that posts to /cart/add).
            scoped = scoped
              .replace(/<product-form\b/gi, '<div data-was-product-form')
              .replace(/<\/product-form>/gi, '</div>');

            this.contentEl.innerHTML = scoped;
            this._reExecuteScripts(this.contentEl);
            this._stripUnneeded();
            this._prependHeroImage();
            this._replaceCtaWithKitButton();
          })
          .catch(() => {
            this.contentEl.innerHTML = '<div class="kit-product-drawer__error">Erreur réseau.</div>';
          });
      }

      close() {
        this.classList.remove('is-open');
        document.body.style.overflow = '';
        if (this._labelObserver) {
          this._labelObserver.disconnect();
          this._labelObserver = null;
        }
        if (this._ctaWrap) {
          this._ctaWrap.remove();
          this._ctaWrap = null;
        }
        if (this.contentEl) this.contentEl.innerHTML = '';
        this.currentSlot = null;
        this.currentProductId = 0;
        this._variants = null;
        this._selectedVariantId = null;
        this._heroImgEl = null;
      }

      // Tries to grab the featured image URL from the section HTML before we mangle it.
      _extractFeaturedImage(scope) {
        // Prefer the gallery's first media item, then the first product image, then any img.
        const candidates = [
          '.product__media-item.is-active img',
          '.product__media-item img',
          '.product__media img',
          '.product img',
        ];
        for (const sel of candidates) {
          const img = scope.querySelector(sel);
          if (img) {
            const src = img.getAttribute('src') || img.getAttribute('data-src');
            if (src) return src;
          }
        }
        return '';
      }

      _prependHeroImage() {
        if (!this._featuredImageUrl) return;
        const hero = document.createElement('div');
        hero.className = 'kit-product-drawer__hero';
        const img = document.createElement('img');
        img.src = this._featuredImageUrl;
        img.alt = '';
        img.loading = 'eager';
        hero.appendChild(img);
        this.contentEl.insertBefore(hero, this.contentEl.firstChild);
        // Cache the img so the variant picker can swap it on selection.
        this._heroImgEl = img;
      }

      _setHeroImage(url) {
        if (!this._heroImgEl) return;
        const next = url || this._featuredImageUrl || '';
        if (!next || this._heroImgEl.getAttribute('src') === next) return;
        this._heroImgEl.setAttribute('src', next);
      }

      // Dawn doesn't expose a product JSON in the section by default — it embeds the variants
      // array inside <variant-selects>. We pull that out so we can resolve a chosen variant
      // back to its full record (price, title, image).
      _extractVariants(scope) {
        const json = scope.querySelector('variant-selects script[type="application/json"]');
        if (!json) return [];
        try {
          const data = JSON.parse(json.textContent);
          return Array.isArray(data) ? data : [];
        } catch (_) { return []; }
      }

      _reExecuteScripts(container) {
        container.querySelectorAll('script').forEach((oldTag) => {
          const newTag = document.createElement('script');
          [...oldTag.attributes].forEach((a) => newTag.setAttribute(a.name, a.value));
          newTag.appendChild(document.createTextNode(oldTag.textContent));
          oldTag.parentNode.replaceChild(newTag, oldTag);
        });
      }

      _stripUnneeded() {
        // Remove things that don't make sense inside the drawer.
        ['pickup-availability', 'product-modal', 'modal-opener', 'product-recommendations',
         '.complementary-products', '.related-products', '.share-button', '.product__view-details',
         '.product__tax', '.breadcrumb', '#ba-loy-container', '.kl-teaser-Wqq4SL',
         '.slider-buttons'].forEach((sel) => {
          this.contentEl.querySelectorAll(sel).forEach((el) => el.remove());
        });

        // Strip every add-to-cart / checkout entry-point Shopify might have rendered, plus
        // the variant picker and quantity input (variant choice happens via the + button on
        // the charm card, not inside the drawer).
        const cartUiSelectors = [
          '.product-form__buttons',
          '.product-form__submit',
          '.shopify-payment-button',
          '[data-shopify="payment-button"]',
          '.product-form__error-message-wrapper',
          'button[name="add"]',
          'button[type="submit"]',
          '.shopify-product-form button[type="submit"]',
          'variant-selects',
          'variant-radios',
          '.product-form__input.product-form__quantity',
          '.product-form__quantity',
          '.product-form__input--quantity',
        ];
        cartUiSelectors.forEach((sel) => {
          this.contentEl.querySelectorAll(sel).forEach((el) => el.remove());
        });
      }

      // Strip Shopify's add-to-cart UI and inject our own CTA — pinned to the bottom of the
      // panel (outside the scrolling content). For multi-variant products, also inject a
      // variant picker right above the CTA so the customer can pick before adding.
      _replaceCtaWithKitButton() {
        const form = this.contentEl.querySelector('form[action*="/cart/add"]');
        if (!form) return;

        form.querySelectorAll(
          '.product-form__buttons, .product-form__submit, [name="add"], button[type="submit"], .shopify-payment-button'
        ).forEach((el) => el.remove());

        // Block any stray submit (Enter key in inputs).
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
        }, true);

        const panel = this.querySelector('.kit-product-drawer__panel');
        if (!panel) return;

        if (this._ctaWrap) {
          this._ctaWrap.remove();
          this._ctaWrap = null;
        }

        const wrapper = document.createElement('div');
        wrapper.className = 'kit-product-drawer__cta-wrap';

        // Variant picker (only for multi-variant products).
        const realVariants = (this._variants || []).filter((v) => v && v.id);
        const isMultiVariant = realVariants.length > 1;
        if (isMultiVariant) {
          const firstAvailable = realVariants.find((v) => v.available) || realVariants[0];
          this._selectedVariantId = firstAvailable ? firstAvailable.id : null;

          // Align the hero image with the pre-selected variant if it has its own image.
          if (firstAvailable) {
            const initialUrl = (firstAvailable.featured_image
                && (firstAvailable.featured_image.src || firstAvailable.featured_image))
              || this._featuredImageUrl;
            this._setHeroImage(initialUrl);
          }

          const variantsWrap = document.createElement('div');
          variantsWrap.className = 'kit-product-drawer__variants';
          variantsWrap.innerHTML = realVariants.map((v) => {
            const disabled = !v.available;
            const isSelected = v.id === this._selectedVariantId;
            const imgUrl = (v.featured_image && (v.featured_image.src || v.featured_image))
              || this._featuredImageUrl
              || '';
            return `
              <button
                type="button"
                class="kit-product-drawer__variant${isSelected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}"
                data-variant-id="${v.id}"
                ${disabled ? 'disabled' : ''}
              >
                ${imgUrl ? `<img src="${imgUrl}" alt="" loading="lazy">` : ''}
                <span class="kit-product-drawer__variant-label">${this._escape(v.title || '')}</span>
                ${disabled ? '<span class="kit-product-drawer__variant-soldout">Épuisé</span>' : ''}
              </button>`;
          }).join('');

          variantsWrap.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-variant-id]');
            if (!btn || btn.disabled) return;
            this._selectedVariantId = parseInt(btn.dataset.variantId);
            variantsWrap.querySelectorAll('[data-variant-id]').forEach((b) => {
              b.classList.toggle('is-selected', b === btn);
            });
            // Swap the hero image to match the picked variant's featured image.
            const picked = realVariants.find((v) => v.id === this._selectedVariantId);
            if (picked) {
              const url = (picked.featured_image && (picked.featured_image.src || picked.featured_image))
                || this._featuredImageUrl;
              this._setHeroImage(url);
            }
          });

          wrapper.appendChild(variantsWrap);
        } else {
          // Single-variant product: pre-select the only variant for the CTA.
          this._selectedVariantId = realVariants[0] ? realVariants[0].id : null;
        }

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'kit-product-drawer__cta';
        btn.textContent = 'Ajouter le charm au kit découverte';
        btn.addEventListener('click', () => this._submitToKit(form));

        wrapper.appendChild(btn);
        panel.appendChild(wrapper);
        this._ctaWrap = wrapper;
      }

      _escape(str) {
        if (!str) return '';
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      }

      _resolveVariantId(form) {
        // 1) Explicit selection from our drawer-injected variant picker.
        if (this._selectedVariantId) return this._selectedVariantId;
        // 2) The form's hidden id input (initial value rendered by Liquid).
        const idInput = form && form.querySelector('[name="id"]');
        if (idInput && idInput.value) {
          const v = parseInt(idInput.value);
          if (v) return v;
        }
        // 3) Mono-variant product.
        if (this._variants && this._variants.length === 1) return this._variants[0].id;
        return 0;
      }

      _submitToKit(form) {
        const variantId = this._resolveVariantId(form);
        if (!variantId) return;

        let title = '';
        let variantTitle = null;
        let price = 0;
        let image = '';

        // Resolve the chosen variant from the cached variants array.
        const variant = (this._variants || []).find((v) => v.id === variantId);
        if (variant) {
          variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : null;
          price = variant.price;
          image = (variant.featured_image && (variant.featured_image.src || variant.featured_image)) || '';
        }

        const h = this.contentEl.querySelector('h1, .product__title');
        if (h) title = h.textContent.trim();
        if (!image) {
          const img = this.contentEl.querySelector('.product__media img, img');
          if (img) image = img.getAttribute('src') || '';
        }
        if (!price) {
          const priceEl = this.contentEl.querySelector('.price-item--regular, [data-product-price]');
          if (priceEl) {
            const cleaned = priceEl.textContent.replace(/[^\d,.\s]/g, '').replace(/\s/g, '').replace(',', '.');
            const num = parseFloat(cleaned);
            if (!isNaN(num)) price = Math.round(num * 100);
          }
        }

        document.dispatchEvent(new CustomEvent('kit-product-drawer:add', {
          detail: {
            slot: this.currentSlot,
            variantId,
            productId: this.currentProductId,
            title: title || 'Charm',
            variantTitle,
            price,
            image,
          },
        }));

        this.close();
      }
    }
  );
}
